import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl, stripOuterParagraph } from '@eneco/cms-types';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Link as NextLink } from '@eneco/next';
import { MessageBar as SparkyMessageBar } from '@eneco/sparky';
import type { MessageBarProps } from '@eneco/sparky/types';

import type { MessageBarContent } from './message-bar-content.types';

/**
 * `MessageBar` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/MessageBar/MessageBar.tsx`): the bar
 * inside the container's `Link`, which hands it the link's `href` (and `target` on an external link), so
 * Sparky's `MessageBar` renders its own link layer. A link in the message text then stays a separate link,
 * not one nested in another.
 *
 * Sitecore's text is bare, which `RichText` keeps at the bar's text size. Sanity's Portable Text always
 * comes out as a paragraph, which `RichText` sets smaller, so on a Sanity page a single one is unwrapped.
 * Both arrive as HTML (`serializeRichText`), hence the page's source; Sitecore's HTML is left as it is.
 */
const MessageBar: FC = () => {
  const { adapter, page } = useCms();
  const content = useContent<MessageBarContent>();
  const headingLevel = useSetting('headingLevel') as MessageBarProps['headingLevel'];

  const richText = adapter.toRichText(content.content);
  const html = page.context.cmsSource === 'sanity' ? stripOuterParagraph(richText) : richText;
  const link = linkWithUrl(content.link);

  const bar = <SparkyMessageBar headingLevel={headingLevel} text={html ? <RichTextComponent html={html} /> : undefined} title={content.title} />;

  return link?.href ? (
    <NextLink linkValue={{ href: link.href, target: link.target }} linkType={link.linkType ?? ''}>
      {bar}
    </NextLink>
  ) : (
    bar
  );
};

export default MessageBar;
